import {
  Component,
  computed,
  inject,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Page } from '../../page/page';
import { SurveyRenderControllerStore } from '../../render-store/render.store';
import { UnifiedSurveyStore } from '../../render-store/unified-survey.store';
import { InteractionTypes } from '@hyper-inferno/survey-elements-core-utils';
import { evaluateManifest } from '../../utils/conditonal-display.evaluator';
import { PluginHost } from '../../plugin-host/plugin-host';
import { ISurveyPlugin } from '../../plugin-host/plugin.interface';

@Component({
  selector: 'lib-survey-page',
  imports: [Page, PluginHost],
  templateUrl: './survey-page.html',
  styleUrl: './survey-page.css',
})
export class SurveyPage {
  YOUR_HARD_CODED_PLUGINS_ARRAY: ISurveyPlugin[] = [
    {
      id: 'page-timer-tracker',
      name: 'Page Time Tracker',
      config: {
        show_timer_to_user: true,
        variable_prefix: 'time_spent_page_',
      },
      variablesSchema: [{ key: 'time_spent_seconds', type: 'number' }],
      html: "<div id='timer-container' style='font-family: sans-serif; font-size: 13px; color: #666; margin-bottom: 10px; display: none;'>Time on page: <span id='timer-display'>0</span>s</div>",
      hooks: {
        onPageInit: `
      context.startTime = Date.now();
      // Accessing the page ID from the renderStore via context
      context.currentPageId = context.registry ? Object.values(context.registry)[0]?.pageId : 'unknown';

      if (context.config.show_timer_to_user) {
        const container = context.container.querySelector('#timer-container');
        const display = context.container.querySelector('#timer-display');
        if(container) container.style.display = 'block';

        context.timerInterval = setInterval(() => {
          const seconds = Math.floor((Date.now() - context.startTime) / 1000);
          if (display) display.innerText = seconds;
        }, 1000);
      }
    `,
        onBeforeSubmit: `
      const totalSeconds = Math.round((Date.now() - context.startTime) / 1000);
      
      // Dynamic key so page 1 and page 2 both get saved
      // Result: { time_spent_page_1: 45, time_spent_page_2: 30 }
      context.setVariable('SCREENTIME_'+context.pageId, totalSeconds);

      if (context.timerInterval) clearInterval(context.timerInterval);
    `,
        onCleanup: `
      if (context.timerInterval) clearInterval(context.timerInterval);
    `,
      },
      version: '',
      configSchema: {},
    },
    {
      id: 'browser-fingerprint',
      name: 'Debug: System Info Collector',
      runOnce: true,
      config: { variable_name: 'debug_metadata' },
      hooks: {
        onPageInit: `
      
      const info = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenSize: \`${window.screen.width}x${window.screen.height}\`,
        windowSize: \`${window.innerWidth}x${window.innerHeight}\`,
        devicePixelRatio: window.devicePixelRatio,
        colorDepth: window.screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        touchPoints: navigator.maxTouchPoints,
        cookiesEnabled: navigator.cookieEnabled,
        timestamp: new Date().toISOString()
      };

      // Also try to detect the specific browser name
      let browserName = 'unknown';
      if (info.userAgent.indexOf('Firefox') > -1) browserName = 'Firefox';
      else if (info.userAgent.indexOf('SamsungBrowser') > -1) browserName = 'Samsung';
      else if (info.userAgent.indexOf('Opera') > -1 || info.userAgent.indexOf('OPR') > -1) browserName = 'Opera';
      else if (info.userAgent.indexOf('Trident') > -1) browserName = 'IE';
      else if (info.userAgent.indexOf('Edge') > -1) browserName = 'Edge';
      else if (info.userAgent.indexOf('Chrome') > -1) browserName = 'Chrome';
      else if (info.userAgent.indexOf('Safari') > -1) browserName = 'Safari';
      
      info.browserName = browserName;

      // Save as a stringified JSON so it occupies one metadata field
      context.setVariable(context.config.variable_name, JSON.stringify(info));
      console.log('System Debug Info Captured:', info);
    
    `,
        onBeforeSubmit: '// This hook will trigger markPluginAsCompleted',
      },
      version: '',
      configSchema: {},
      variablesSchema: [],
    },
  ];

  // Transform your hard-coded array into a Signal
  readonly Iplugins = computed(() => {
    const currentPage = this.surveyRenderStore.currentPageState()?.page;

    // If no page, no plugins
    if (!currentPage) return [];

    // Return your hard-coded list
    // By returning a NEW array reference here, you help the @for loop notice a change
    return [...this.YOUR_HARD_CODED_PLUGINS_ARRAY];
  });
  @ViewChildren('pluginRef') plugins!: QueryList<PluginHost>;
  onPageAction($event: { type: InteractionTypes; payload?: any }) {
    switch ($event.type) {
      case InteractionTypes.SUBMIT:
        return this.onSubmit();
      case InteractionTypes.SWITCH:
        return this.surveyBotController.onSwitchQuestionClick(
          $event.payload ?? false,
        );
      // case InteractionTypes.LOGIN:
      //   return this.onLoginClick(event.payload);
      default:
        break;
    }
  }
  surveyRenderStore = inject(SurveyRenderControllerStore);
  surveyBotController = inject(UnifiedSurveyStore);

  onSubmit() {
    this.surveyRenderStore.attemptSubmit();

    const pageElements =
      this.surveyRenderStore.currentPageState?.().page?.elements ?? [];
    const registry = this.surveyRenderStore.registry();

    let pageValid = true;

    pageElements.forEach((el) => {
      const id = String(el.id);
      const entry = registry[id];

      // 1. CHECK VISIBILITY FIRST
      // If the element shouldn't be displayed, we skip validation entirely
      const isVisible = el.conditional_display_manifest
        ? evaluateManifest(el.conditional_display_manifest, registry)
        : true;

      if (!isVisible) {
        return; // Skip this element; it's hidden logic-wise
      }

      // 2. VALIDATE ONLY VISIBLE ELEMENTS
      if (entry && typeof entry.validate === 'function') {
        const isElementValid = entry.validate();
        if (!isElementValid) {
          pageValid = false;
        }
      }
    });

    this.plugins.forEach((p) => p.runSubmitHook());

    if (pageValid) {
      this.surveyBotController.onSubmitButtonClick();
    }
  }
}
